import Animation from "./Animation";
import { gsap, Sine } from "gsap";
import { arrayShuffle } from "../helpers";

class ImagesOnBoundary extends Animation {

	/**
	 * Runs the animation
	 * @param {Function} done animation complete callback, passed into run()
	 */
	animate({ done }) {

		gsap.fromTo(this.animEls, {
			opacity: 0,
			top: `${this.animTopShift}px`
		}, {
			opacity: 1,
			delay: 0.3,
			duration: 0.6,
			top: 0,
			ease: "Power2.inOut",
			stagger: 0.15 / this.density,
			onComplete: done
		});
	}

	/**
	 * Will fire when element binds to the DOM, use this to
	 * set up default states if needed
	 */
	setDefault() {
		this.desktopOnly = this.element.dataset.desktop === "true"
		if (this.desktopOnly && window.Zero.windowWidth < 768) return;

		/** --- Settings --- */
		this.enableAnim = true; // do we want entrance animation
		this.animTopShift = 30;
		this.parent = this.element.closest("section");

		this.density = parseInt(this.element.dataset.density, 10)
		this.padding = this.element.dataset.padding === "true"
		let scale = parseInt(this.element.dataset.scale, 10)
		this.scale = window.Zero.isMobile
			? this.element.dataset.scaleMobile !== ""
				? parseInt(this.element.dataset.scaleMobile, 10)
				: scale
			: scale

		this.scrollRunning = false;

		this.imageThreshold = (window.Zero.windowWidth > 768) ? (400 / this.density) : (300 / this.density); // the image threshold used to know how many images can be on every edge
		this.boundaryThreshold = (window.Zero.windowWidth > 768) ? 60 : 30; // how close can the points between images be from each other and edges

		// Inset
		this.boundaryMultiplier = 0.8; // percentage of range from offset

		// This is overridden on mobile
		this.insetValue = 90
		this.topInset = this.padding ? 0 : -this.insetValue
		this.rightInset = this.padding ? -this.insetValue : -this.insetValue * 2
		this.bottomInset = this.padding ? -this.insetValue * 2 : -this.insetValue
		this.leftInset = this.padding ? -this.insetValue : 0

		/** --- Default states for animation or element can go in here --- */
		this.bounding = {
			height: this.element.clientHeight,
			width: this.element.clientWidth,
		};

		this.capacity = {
			x: Math.floor(this.bounding.width / this.imageThreshold),
			y: Math.floor(this.bounding.height / this.imageThreshold)
		};

		this.input = arrayShuffle(JSON.parse(this.element.dataset.images));

		// bounce if empty
		if (!this.input || this.input.length < 1) return;

		this.products = [...new Set(this.input.map(o => o.product_id))];
		this.totalImages = (this.capacity.x * 2) + (this.capacity.y * 2);
		this.imagesPerProduct = Math.ceil(this.totalImages / this.products.length);

		this.images = this.generateImageObject(this.input);

		// output imagery
		this.writeImages();

		// store the image wrapper and images
		this.imageWrap = this.element.querySelector(".image-frame-inner");
		this.animEls = this.imageWrap.querySelectorAll("img");

		// refresh lazy load
		window.Zero.lazyLoad.update();
	}

	/**
	 * Generate the image objects
	 * @param {*} data
	 * @returns
	 */
	generateImageObject(data) {

		let array = [];
		let check = {
			p: 0,
			v: {},
			i: {	// Check what sides are holding images
				t: 0,
				r: 0,
				b: 0,
				l: 0
			}
		};

		output: while (array.length < this.totalImages) { // 3

			for (let i = 0; i < this.input.length; i++) { // 3

				let obj = {};
				let img = this.input[i];

				if (!check.v[img.id] || check.v[img.id] < this.imagesPerProduct) {

					//
					let range = (this.imageThreshold - (this.imageThreshold * this.boundaryMultiplier)) - this.boundaryThreshold;
					let max, min;
					let x, y;
					let modulo = Math.ceil(check.p % 4);

					// Sides:
					// Top
					if (modulo == 0 && this.capacity.x > check.i.t) {

						max = (this.imageThreshold + this.boundaryThreshold) * (check.i.t + 1);
						min = range + ((check.i.t * this.imageThreshold) + (this.boundaryThreshold * (check.i.t + 1)));

						x = Math.floor(Math.random() * (max - min + 1)) + min;
						y = (window.Zero.windowWidth > 768) ? this.topInset : 0;

						++check.i.t;

						// Right
					} else if (modulo == 1 && this.capacity.y > check.i.r) {

						max = (this.imageThreshold + this.boundaryThreshold) * (check.i.r + 1);
						min = range + ((check.i.r * this.imageThreshold) + (this.boundaryThreshold * (check.i.r + 1)));

						x = (window.Zero.windowWidth > 768) ? (this.bounding.width + this.rightInset) : this.bounding.width;
						y = Math.floor(Math.random() * (max - min + 1)) + min;

						++check.i.r;

						// Bottom
					} else if (modulo == 2 && this.capacity.x > check.i.b) {

						max = (this.imageThreshold + this.boundaryThreshold) * (check.i.b + 1);
						min = range + ((check.i.b * this.imageThreshold) + (this.boundaryThreshold * (check.i.b + 1)));

						x = Math.floor(Math.random() * (max - min + 1)) + min;

						y = (window.Zero.windowWidth > 768) ? (this.bounding.height + this.bottomInset) : this.bounding.height;

						++check.i.b;

						// Left
					} else if (modulo == 3 && this.capacity.y > check.i.l) {

						max = (this.imageThreshold + this.boundaryThreshold) * (check.i.l + 1);
						// min = range + ((check.i.l + 1) * this.boundaryThreshold);
						min = range + ((check.i.l * this.imageThreshold) + (this.boundaryThreshold * (check.i.l + 1)));

						x = (window.Zero.windowWidth > 768) ? this.leftInset : 0;
						y = Math.floor(Math.random() * (max - min + 1)) + min;

						++check.i.l;
					}

					// iterate to keep modulo accurate
					++check.p;

					// only push if we can plot
					if (x >= 0 || y >= 0) {

						obj.url = img.url;
						obj.alt = img.alt;
						obj.h = img.height;
						obj.w = img.width;
						obj.a = img.aspect_ratio;
						obj.x = x; // x position
						obj.y = y; // y position
						obj.r = Math.floor(Math.random() * (180)) - 90; // rotation

						obj.s = this.scale / 100; // scale (Input is range of 80-150 so we divide by 100 to get range of 0.8 to 1.5 for css transform)
						obj.k = 0; // skew

						array.push(obj);
					}

					// Exit while loop while inside forloop
					if (array.length >= this.totalImages) break output;
				}
			}
		}

		return array;
	}

	/**
	 * writeImages
	 */
	writeImages() {

		let div = document.createElement("div");
		let inner = document.createElement("div");

		div.classList = "image-frame";
		inner.classList = "image-frame-inner";

		div.appendChild(inner);

		this.element.prepend(div);

		[...this.images].forEach((object) => {

			let wrap = document.createElement("div");
			let img = document.createElement("img");
			let style = `--img-k: ${object.k}; --img-r: ${object.r}deg; --img-s: ${object.s};`;

			wrap.classList = "image-object";

			wrap.style = `--img-x: ${object.x}px; --img-y: ${object.y}px;`;

			img.classList = "lazy";
			img.alt = object.alt;
			img.src = object.url.replace("width=400", "width=10");
			img.dataset.src = object.url;

			if (this.enableAnim === true) {
				style += ` opacity: 0; top: ${this.animTopShift}px;`
			}

			img.style = style;

			wrap.appendChild(img);
			inner.appendChild(wrap);
		});
	}

	/**
	 * Intersection Observer callback, only needs to fire this.run()
	 * this.run() can contain a callback function for done()
	 */
	onViewportEnter() {

		if (!this.enableAnim) return;

		/** --- Fire the animation with a callback function for done() --- */
		this.run(() => {

		});
	}

	/**
	 * Intersection Observer callback for exit
	 * can be used to reset animation (timeline?)
	 */
	onViewportExit() {

	}

	/**
	 * 1-to-1 scroll binding
	 */
	onScroll(scroll) {

		if (this.scrollRunning == false) return;

	}
}

export default ImagesOnBoundary;
